/** @jsxImportSource @emotion/react */
import { useApolloClient } from '@apollo/client';
import { Box, FlexLayout, H3, H4 } from '@deity/falcon-ui';
import { KeyNameValidator } from '@deity-io/di-data-basics';
import { Button, Icon } from '@deity-io/portal-ui';
import { Portal } from '@material-ui/core';
import { Alert } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import type { SchemaActions } from 'contexts/SchemaContext';
import { SchemaActionTypes, SchemaContext } from 'contexts/SchemaContext';
import { UIContext } from 'contexts/UIContext';
import type { ISchema } from 'contracts/interfaces';
import _, { isEqual } from 'lodash';
import type React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { useGetModelQuery, useValidateStructureSchemaMutation } from 'services/graphql/generated/hooks';
import { isValidateRootField } from 'utils';
import SchemaMemo from './SchemaMemo';

type SchemaContainerProps = {
  locked?: boolean;
  params: {
    versionUuid: string;
    entityId: string;
  };
};
const SchemaContainer: React.FC<SchemaContainerProps> = ({ locked, params }) => {
  const client = useApolloClient();
  const schemaUploadRef = useRef<HTMLInputElement>(null);
  const ctx = useContext(SchemaContext);
  const UICtx = useContext(UIContext);
  const [isSchemaEmpty, setSchemaEmpty] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const { data: modelData } = useGetModelQuery({
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: params.entityId
    },
    fetchPolicy: 'cache-first'
  });

  const [validateSchema, { data: validateResult, loading: validating }] = useValidateStructureSchemaMutation();

  useEffect(() => {
    if (ctx?.state.schema) {
      if (Object.keys(ctx?.state.schema).length === 0) {
        setSchemaEmpty(true);
      } else {
        setSchemaEmpty(false);
      }
    }
  }, [ctx?.state.schema, ctx?.state.selectedField]);

  const onFileInput = (fileInputEvent: any) => {
    fileInputEvent.stopPropagation();
    fileInputEvent.preventDefault();
    const reader = new FileReader();
    const file = fileInputEvent.target.files[0];
    reader.addEventListener('loadend', (loadendEvent: any) => {
      const schema = loadendEvent.target.result;
      let parsedSchema;
      try {
        parsedSchema = JSON.parse(schema);
      } catch (loadendError) {
        setShowSnackbar(true);
      }
      if (typeof parsedSchema === 'object') {
        if (isValidateRootField(parsedSchema)) {
          validateSchema({
            variables: {
              schema: parsedSchema
            }
          }).then(response => {
            if (response?.data?.validateStructureSchema?.isValid) {
              ctx?.dispatch({
                type: SchemaActionTypes.INIT_SCHEMA,
                payload: {
                  schema: parsedSchema as ISchema,
                  uuid: params.entityId,
                  locked: ctx.state.locked,
                  name: ctx.state.name
                }
              });
            } else {
              setShowSnackbar(true);
            }
          });
        } else {
          setShowSnackbar(true);
        }
      }
      const fileInput = schemaUploadRef?.current;
      if (fileInput) {
        fileInput.value = '';
      }
    });
    reader.readAsText(file);
  };

  const onUpload = e => {
    if (schemaUploadRef != null) {
      (schemaUploadRef as any).current.click();
      e.target.blur();
    }
  };

  const handleClose = () => {
    setShowSnackbar(false);
  };

  const handleFieldAction = (action: SchemaActions): void => ctx?.dispatch(action);

  const validateKey = (path: Array<string>, key: string): boolean => {
    const parent = _.get(ctx?.state.schema, path.slice(0, -1)?.join('.'));
    if (parent && typeof parent === 'object' && key in parent) {
      return false;
    }

    return KeyNameValidator.test(key);
  };

  return (
    <Box height="full">
      <Prompt
        when={!isEqual(modelData?.getModel.schema, ctx?.state.schema)}
        message={(location, action) => `Changes that you made will not be saved.`}
      />
      <Portal>
        <Snackbar style={{ marginBottom: '60px' }} open={showSnackbar} autoHideDuration={60000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
            Invalid structure schema!
          </Alert>
        </Snackbar>
      </Portal>
      <div
        css={{
          height: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <FlexLayout p="xxl" justifyContent="space-between" alignItems="center">
          <Box>
            <H4
              fontSize={'xll' as any}
              pt="xss"
              pb="xss"
              fontWeight="normal"
              mr="lg"
              border="regular"
              borderColor="transparent"
            >
              {modelData?.getModel.name}
            </H4>
            {isSchemaEmpty && <H3 color="secondary">Please add a root node first to start modelling your schema.</H3>}
          </Box>
          {locked !== true && (
            <Box>
              <input
                ref={schemaUploadRef}
                onChange={onFileInput}
                type="file"
                style={{ display: 'none' }}
                accept="application/JSON"
              />
              <Button variant="outlined" sx={{ ml: 2 }} startIcon={<Icon icon="upload" />} onClick={onUpload}>
                {validating ? 'Validating schema...' : 'Upload schema'}
              </Button>
            </Box>
          )}
        </FlexLayout>
        <FlexLayout
          height="full"
          width="full"
          bg="white"
          flexDirection="column"
          justifyContent="space-between"
          css={{
            boxSizing: 'border-box'
          }}
        >
          <Box
            css={{
              height: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: '96px',
              paddingBottom: '2em'
            }}
          >
            {ctx?.state.schema && modelData?.getModel.name && (
              <SchemaMemo
                name={modelData?.getModel.name}
                schema={ctx?.state.schema}
                selectedField={ctx?.state.selectedField || []}
                handleFieldAction={handleFieldAction}
                validateKey={validateKey}
                locked={locked}
                onSelect={path => {
                  ctx?.dispatch({
                    type: SchemaActionTypes.SELECT_FIELD,
                    payload: { path }
                  });
                }}
              />
            )}
          </Box>
        </FlexLayout>
      </div>
    </Box>
  );
};

export default SchemaContainer;
